import classNames from "classnames";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import InputSmily from "@Components/atoms/InputSmily";
import Link from "@Components/atoms/Link";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconTrash } from "@Icons/16x16/trash.svg";
import { ReactComponent as IconDown } from "@Icons/down.svg";
import { ReactComponent as IconShield } from "@Icons/shield.svg";
import { Image } from "@Types/images";
import { PriceFormatted } from "@Types/price";
import { paymentTypes } from "@Utils/cart/payment";
import "./style.scss";

interface Props {
  children?: ReactNode;
  contentType: "articles" | "promoCode";
  disabled: boolean;
  displayContent: boolean;
  displayPromoCodeInput?: boolean;
  estimatedShippingCost: boolean;
  hasOutOfStockProduct?: boolean;
  isFreeShippingBannerDisplayed?: boolean;
  onClickButton?: () => void;
  onClickPromoCodeLink?: () => void;
  onClickPromoCodesList?: () => void;
  onClickTitle: () => void;
  onSubmitPromoCode: (code: string | undefined) => Promise<void>;
  promoCode: string | undefined;
  promoCodeErrorMessage: string | undefined;
  promoTotal: PriceFormatted;
  shippingTotal: PriceFormatted;
  subTotal: PriceFormatted;
  total: PriceFormatted;
}

const PurchaseSummary: FunctionComponent<Props> = ({
  children,
  contentType,
  disabled,
  displayContent,
  displayPromoCodeInput,
  estimatedShippingCost,
  hasOutOfStockProduct = false,
  isFreeShippingBannerDisplayed = false,
  onClickButton,
  onClickPromoCodeLink,
  onClickPromoCodesList,
  onClickTitle,
  onSubmitPromoCode,
  promoCode,
  promoCodeErrorMessage,
  promoTotal,
  shippingTotal,
  subTotal,
  total,
}) => {
  const { isLargeScreen } = useBreakpoint();
  const intl = useIntl();
  const [promoCodeInput, setPromoCodeInput] = useState<string>("");

  useEffect(() => {
    promoCode && setPromoCodeInput("");
  }, [promoCode]);

  const purchaseContentPromoCodeInput = (
    <div
      className={classNames("PurchaseSummary-content-promoCode-input", {
        "PurchaseSummary-content-promoCode-input--expand": displayPromoCodeInput,
      })}
      style={{
        maxHeight: displayContent || displayPromoCodeInput ? "200px" : 0,
      }}
    >
      {contentType === "articles" && (
        <span className="PurchaseSummary-content-subTotal">
          {intl.formatMessage({ id: "cheerz_PurchaseSummary_promoCode_link" })}
        </span>
      )}
      {intl.formatMessage({ id: "cheerz_CartCheckoutStep_promoCode_info" })}
      {contentType === "promoCode" && (
        <span
          className="PurchaseSummary-content-promoCode-input-link"
          onClick={onClickPromoCodesList}
        >
          {intl.formatMessage({ id: "cheerz_CartCheckoutStep_promoCode_list" })}
        </span>
      )}
      <div className="PurchaseSummary-content-promoCode-input-form">
        <InputSmily
          disabled={disabled}
          errorMessage={promoCodeErrorMessage}
          onChange={(value) => setPromoCodeInput(value)}
          onDismiss={() => setPromoCodeInput("")}
          onValidate={() => onSubmitPromoCode(promoCodeInput.toUpperCase())}
          placeholder={intl.formatMessage({ id: "cheerz_PurchaseSummary_promoCode_input" })}
          type="text"
          value={promoCodeInput}
        />
        <ButtonSmily
          disabled={disabled || promoCodeInput === ""}
          onClick={() => onSubmitPromoCode(promoCodeInput.toUpperCase())}
          size="s"
          type="secondary"
        >
          {intl.formatMessage({ id: "cheerz_CartFreeShippingBanner_apply" })}
        </ButtonSmily>
      </div>
    </div>
  );

  const purchaseContentPromoCodeValue = !!promoCode ? (
    <div className="PurchaseSummary-content-promoCode-value">
      <span className="PurchaseSummary-content-promoCode-value-code">
        {`"${promoCode}"`}
        <ButtonSmily
          disabled={disabled}
          icon={<IconTrash />}
          iconPosition="alone"
          onClick={() => onSubmitPromoCode(undefined)}
          size="xs"
          type="secondary"
        />
      </span>
      <div className="PurchaseSummary-content-promoCode-value-price">
        {intl.formatNumber(promoTotal.cents / 100, {
          currency: promoTotal.currency,
          style: "currency",
        })}
      </div>
    </div>
  ) : !displayPromoCodeInput && contentType === "articles" ? (
    <Link onClick={onClickPromoCodeLink} size="m">
      {intl.formatMessage({ id: "cheerz_PurchaseSummary_promoCode_link" })}
    </Link>
  ) : (
    contentType === "articles" && purchaseContentPromoCodeInput
  );
  return (
    <div
      className={classNames("PurchaseSummary", {
        "PurchaseSummary--small": contentType === "articles" && !isLargeScreen,
      })}
      style={{
        top: isLargeScreen
          ? isFreeShippingBannerDisplayed
            ? "144px"
            : contentType === "articles"
            ? "109px"
            : "64px"
          : "0",
      }} // These values depend on HeaderCart and CartFreeShippingBanner TODO: Change these values when remove CartBreadcrumb
    >
      {isLargeScreen && (
        <div className="PurchaseSummary-title">
          {intl.formatMessage({ id: "cheerz_PurchaseSummary_title" })}
        </div>
      )}

      <div
        className={classNames("PurchaseSummary-content", {
          "PurchaseSummary-content--open": displayContent,
        })}
      >
        <div className="PurchaseSummary-content-title" onClick={onClickTitle}>
          {contentType === "articles"
            ? intl.formatMessage({ id: "cheerz_HeaderMobileCart_title" })
            : intl.formatMessage({ id: "cheerz_PurchaseSummary_promoCode_title" })}
          <IconDown />
        </div>

        {!!children ? children : contentType === "promoCode" && purchaseContentPromoCodeInput}

        <div
          className="PurchaseSummary-content-details"
          style={{
            maxHeight: displayContent || isLargeScreen || contentType === "promoCode" ? "300px" : 0,
          }}
        >
          <div className="PurchaseSummary-content-subTotal">
            {intl.formatMessage({ id: "cheerz_PurchaseSummary_subTotal" })}
            <div className="PurchaseSummary-content-subTotal-price">
              {intl.formatNumber(subTotal.cents / 100, {
                currency: subTotal.currency,
                style: "currency",
              })}
            </div>
          </div>

          {contentType === "promoCode" && purchaseContentPromoCodeValue}

          <div
            className={classNames("PurchaseSummary-content-subTotal", {
              "PurchaseSummary-content-subTotal--estimated":
                contentType === "articles" && estimatedShippingCost,
            })}
          >
            {estimatedShippingCost
              ? intl.formatMessage({ id: "cheerz_PurchaseSummary_estimated_shipping" })
              : intl.formatMessage({ id: "cheerz_PurchaseSummary_shipping_costs" })}
            <div className="PurchaseSummary-content-subTotal-price">
              {intl.formatNumber(shippingTotal.cents / 100, {
                currency: shippingTotal.currency,
                style: "currency",
              })}
            </div>
          </div>

          {contentType === "articles" && purchaseContentPromoCodeValue}

          {contentType === "promoCode" && (
            <div className="PurchaseSummary-content-subTotal-info">
              {intl.formatMessage({ id: "cheerz_PurchaseSummary_estimated_shipping_info" })}
            </div>
          )}
        </div>

        <div className="PurchaseSummary-content-total">
          {intl.formatMessage({ id: "cheerz_PurchaseSummary_total" })}
          <div className="PurchaseSummary-content-total-price">
            {intl.formatNumber(total.cents / 100, {
              currency: total.currency,
              style: "currency",
            })}
          </div>
        </div>

        {contentType === "promoCode" && (
          <div className="PurchaseSummary-content-checkout">
            <ButtonSmily
              disabled={disabled || hasOutOfStockProduct}
              onClick={onClickButton}
              size="m"
              type="primary"
            >
              {intl.formatMessage({ id: "cheerz_PurchaseSummary_checkout" })}
            </ButtonSmily>

            {hasOutOfStockProduct && (
              <div className="PurchaseSummary-content-checkout-outOfStock">
                {intl.formatMessage({ id: "cheerz_PurchaseSummary_outOfStock" })}
              </div>
            )}
          </div>
        )}
      </div>

      {isLargeScreen && (
        <div className="PurchaseSummary-paymentInfos">
          {paymentTypes.map(({ name, src }: Image) => (
            <img alt={name} className="PurchaseSummary-paymentInfos-logo" key={name} src={src} />
          ))}
          <div className="PurchaseSummary-paymentInfos-title">
            {intl.formatMessage({ id: "cheerz_PurchaseSummary_payment_infos" })}
            <IconShield />
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseSummary;
